import {
  Smartphone, Zap, RefreshCw, ShieldCheck,
  Gem, Sparkles, CreditCard, Users, Truck, BarChart3,
} from "lucide-react";
import metalHero from "@/assets/products/metal-hero.webp";
import metalFeatures from "@/assets/products/metal-features.webp";
import metalTeam from "@/assets/products/metal-team.webp";
import metalProfile from "@/assets/products/metal-profile.webp";
import type { ProductPageDict } from "@/lib/shop/product-dict";

const SLUG = "metal";
const SKU = "BC-METAL";
const BASE_PRICE = 72;
const PRODUCT_URL_EN = "https://banana.cards/shop/metal";
const PRODUCT_URL_VI = "https://banana.cards/vi/shop/metal";

const RATING = { value: "4.87", count: "68" };

const BUNDLES = [
  { qty: 1,  label: "1 card",  perCard: 1.0 },
  { qty: 3,  label: "3 cards", perCard: 0.9,  save: 10, badge: "Save 10%" },
  { qty: 5,  label: "5 cards", perCard: 0.82, save: 18, badge: "Most popular" },
  { qty: 10, label: "10 cards", perCard: 0.72, save: 28, badge: "Best value" },
] as const;

const BUNDLES_VI = [
  { qty: 1,  label: "1 thẻ",   perCard: 1.0 },
  { qty: 3,  label: "3 thẻ",   perCard: 0.9,  save: 10, badge: "Tiết kiệm 10%" },
  { qty: 5,  label: "5 thẻ",   perCard: 0.82, save: 18, badge: "Phổ biến nhất" },
  { qty: 10, label: "10 thẻ",  perCard: 0.72, save: 28, badge: "Giá trị nhất" },
] as const;

const GALLERY = [
  { src: metalHero,     alt: "Custom Metal AI business card front and back with NFC and QR" },
  { src: metalFeatures, alt: "Metal card features — engraved logo, NFC chip, QR code" },
  { src: metalProfile,  alt: "Customizable digital profiles on iPhone" },
  { src: metalTeam,     alt: "Team & enterprise control dashboard" },
];

const GALLERY_VI = [
  { src: metalHero,     alt: "Thẻ kim loại AI mặt trước và sau với NFC và mã QR" },
  { src: metalFeatures, alt: "Cấu tạo thẻ — logo khắc laser, chip NFC, mã QR" },
  { src: metalProfile,  alt: "Hồ sơ kỹ thuật số tùy chỉnh trên iPhone" },
  { src: metalTeam,     alt: "Bảng điều khiển dành cho đội nhóm và doanh nghiệp" },
];

export const metalDict: ProductPageDict = {
  slug: SLUG,
  routePath: "/shop/metal",
  productUrl: PRODUCT_URL_EN,
  basePrice: BASE_PRICE,
  sku: SKU,
  rating: RATING,
  seo: {
    title: "Custom Metal AI Business Card — banana.cards",
    description: "Premium black powder-coated metal NFC business card with engraved logo, QR code and AI-powered profile sharing. Built to last, designed to impress.",
    ogTitle: "Custom Metal AI Business Card",
    ogDescription: "Premium metal NFC business card. Tap once, share instantly, capture every contact — AI follow-ups built in.",
  },
  jsonLdDescription:
    "Engineered for impact, the Custom Metal AI Business Card delivers unmatched durability and prestige. Precision-cut stainless steel with a black powder coat, embedded NFC chip and laser-engraved logo linked to your AI-powered banana.cards profile.",
  eyebrow: "banana.cards · Metal",
  productName: "Custom Metal AI Business Card",
  productIntro:
    "Engineered for impact, the Custom Metal AI Business Card delivers unmatched durability and prestige. Precision-cut stainless steel combined with NFC technology creates a card that feels as powerful as it looks — designed for professionals who want to stand out, every single tap.",
  ratingLabel: "4.87 · 68 reviews",
  perCardSuffix: "/ card · Free worldwide shipping over $50",
  saveBadge: (pct) => `Save ${pct}%`,
  bundles: BUNDLES,
  bundleSection: {
    eyebrow: "Bundle & save",
    heading: { pre: "Equip your team in", em: "premium metal." },
    sub: "Each card carries its own profile — perfect for co-founders, sales teams or whole companies.",
    bundleLabel: "Bundle & save",
    perCardLabel: "per card",
    selected: "Selected",
    choose: "Choose",
  },
  usps: [
    { icon: Smartphone, label: "Share your details with one tap" },
    { icon: Zap,        label: "Collect their details back with one click" },
    { icon: RefreshCw,  label: "No app needed — works on both Apple & Android" },
    { icon: ShieldCheck,label: "Lifetime hardware guarantee" },
  ],
  features: {
    eyebrow: "Why metal",
    heading: { pre: "Built like a vault.", em: "Smart like an assistant." },
    items: [
      { icon: Gem,        title: "Premium stainless steel",   desc: "Precision-cut metal with a black powder coat finish — weighted, scratch-resistant and built to last." },
      { icon: Sparkles,   title: "AI follow-ups built-in",    desc: "Every tap lands in your banana CRM, enriched and ready for AI-drafted follow-ups." },
      { icon: CreditCard, title: "Laser-engraved logo",       desc: "Your logo, name and accent details engraved permanently — won't fade, won't rub off." },
      { icon: Users,      title: "Team-friendly rollout",     desc: "Bulk metal orders for whole teams — branded, fulfilled and billed in one go." },
      { icon: Truck,      title: "Free worldwide shipping",   desc: "Orders over $50 ship free to 100+ countries — 3-5 business days." },
      { icon: BarChart3,  title: "Real-time analytics",       desc: "See every tap, click and lead in your dashboard — know which events actually convert." },
    ],
  },
  anatomy: {
    eyebrow: "Card anatomy",
    heading: { pre: "Every detail,", em: "engineered." },
    image: metalFeatures,
    imageAlt: "Anatomy of the Custom Metal AI business card — engraved logo, NFC chip and QR code",
    items: [
      { t: "Your custom engraved logo", d: "Laser-engraved directly onto the powder-coated surface — permanent, premium, unmistakably yours." },
      { t: "Black powder coated metal", d: "Stainless steel core with a matte black powder coat — resists fingerprints, scratches and bending." },
      { t: "Embedded NFC chip", d: "Tap to share your full profile to any modern iPhone or Android — no app required." },
      { t: "Printed QR code", d: "A perfect fallback for older devices or thicker phone cases — every introduction lands." },
    ],
  },
  profile: {
    eyebrow: "Your profile, your way",
    heading: { pre: "One card.", em: "Infinite identities." },
    body: "Pick a layout, add your colors, photo, links, services and CTAs — and switch between work, side-hustle or recruiting profiles from the same metal card.",
    bullets: [
      "12+ designer-made layouts & themes",
      "Add video, gallery, services, FAQs",
      "Multi-language profiles",
      "Lead-capture form on every card",
    ],
    image: metalProfile,
    imageAlt: "Customisable digital profiles on iPhone",
  },
  compare: {
    eyebrow: "Paper vs Metal",
    heading: { pre: "Paper cards get lost.", em: "Metal gets remembered." },
    sub: "You deserve something that actually works — and that people actually keep.",
    paperLabel: "Paper business cards",
    productLabel: "Custom Metal AI Business Card",
    paper: [
      "Hand out paper cards and hope they keep it",
      "Manually follow up days later",
      "Forget who you met or what you said",
      "No analytics, no CRM, no visibility",
      "You look like everyone else",
    ],
    product: [
      "Tap once, your details save instantly",
      "Automatic smart follow-ups",
      "Every contact captured automatically",
      "Real-time analytics show which events convert",
      "Modern, premium, unforgettable",
    ],
  },
  steps: {
    eyebrow: "How it works",
    heading: { pre: "You don't need to explain.", em: "You just tap." },
    sub: "They get everything that matters — instantly.",
    stepLabel: (n) => `Step ${n}`,
    items: [
      { title: "Tap once to share easily",   desc: "Tap your NFC Banana Card on any smartphone, no app required. Your profile, links and calendar open instantly." },
      { title: "They save you instantly",    desc: "They add you straight to their contacts — your info stays where they actually look: their phone." },
      { title: "You get their details back", desc: "Each new lead lands automatically in your Banana dashboard and syncs to your CRM — no manual entry." },
    ],
  },
  team: {
    eyebrow: "For teams",
    heading: { pre: "Built for", em: "teams that close." },
    body: "Roll out branded metal cards across your whole team, manage every profile from one dashboard, and route every new contact straight into your shared CRM.",
    cta: "Order for your team",
    image: metalTeam,
    imageAlt: "Team and enterprise control dashboard",
    teamsHref: "/teams",
  },
  faq: {
    eyebrow: "FAQ",
    heading: { pre: "Frequently asked", em: "questions." },
    items: [
      { q: "What makes the Custom Metal AI Business Card different from plastic cards?", a: "The Custom Metal AI Business Card is engineered from premium-grade stainless steel, providing a weighted, high-quality feel that plastic cards cannot match. Beyond the physical durability, it is integrated with Banana's AI ecosystem, turning a standard hand-off into an automated lead capture and follow-up sequence." },
      { q: "How do I customize my metal NFC business card?", a: "After checkout we'll send you a short design brief. Upload your logo, pick accent colors and any text you want engraved — our team mocks it up, you approve, we produce. Most cards ship within 5–7 business days of design approval." },
      { q: "Does the metal card work through phone cases?", a: "Yes. The embedded NFC antenna is tuned to read reliably through most standard silicone, leather and plastic cases. For very thick or metal-backed cases, you can also share with the printed QR code on the reverse." },
      { q: "What digital features come with the Metal AI card?", a: "Every card includes a banana.cards profile: unlimited links, multi-language profiles, lead capture form, Apple & Google Wallet pass, real-time analytics, CRM sync, and access to our AI tools (writer, enrichment, follow-up drafts)." },
      { q: "How does the AI follow-up feature work?", a: "When someone taps your card, their details are enriched (company, role, signals) and pushed into your dashboard. Our AI then drafts a personalized follow-up based on the context of the meeting — you review, edit and send in seconds." },
      { q: "Is the laser engraving on the metal card permanent?", a: "Absolutely. The engraving is etched directly into the metal surface, so it won't fade, rub off, or wear down — it's part of the card itself and is covered by our lifetime hardware guarantee." },
      { q: "Can I use the Metal AI card with my existing CRM?", a: "Yes. Banana syncs natively with HubSpot, Salesforce, Pipedrive, Notion, Google Contacts and 50+ tools via Zapier — every new contact flows straight into the system you already use." },
      { q: "Do I need to charge my metal card or use batteries?", a: "No. The NFC chip is passive — it draws a tiny amount of power from the smartphone reading it. There are no batteries to replace and nothing to charge. Ever." },
      { q: "Can I track how many times my metal card is used?", a: "Yes. Your dashboard shows every tap and QR scan in real time — including location, device, time of day and which links got clicked — so you can measure exactly which events and meetings drive results." },
      { q: "Is there a digital backup if I don't have my physical card?", a: "Yes. Your profile lives at a unique URL that works from any device — share it via link, QR, email signature or Apple/Google Wallet pass. The metal card is the experience, but you're never locked out without it." },
    ],
  },
  outro: {
    heading: { pre: "Network with", em: "premium on your side." },
    body: "Order before 3pm and we ship the next business day. Free worldwide shipping over $50.",
    primaryCta: "Get my Metal card",
    secondaryCta: "See all products",
    shopHref: "/shop",
  },
  buyBox: {
    addToCart: "Add to cart",
  },
  gallery: GALLERY,
};

export const metalDictVi: ProductPageDict = {
  slug: SLUG,
  routePath: "/vi/shop/metal",
  productUrl: PRODUCT_URL_VI,
  basePrice: BASE_PRICE,
  sku: SKU,
  rating: RATING,
  seo: {
    title: "Thẻ Danh Thiếp AI Kim Loại Tùy Chỉnh — banana.cards",
    description: "Thẻ danh thiếp NFC kim loại phủ sơn tĩnh điện đen cao cấp, khắc logo, mã QR và chia sẻ hồ sơ bằng AI. Bền đẹp, tạo ấn tượng mạnh.",
    ogTitle: "Thẻ Danh Thiếp AI Kim Loại Tùy Chỉnh",
    ogDescription: "Thẻ danh thiếp NFC kim loại cao cấp. Chạm một lần, chia sẻ tức thì, thu thập mọi liên hệ — tích hợp AI theo dõi tự động.",
    locale: "vi_VN",
  },
  jsonLdDescription:
    "Được chế tác để tạo ấn tượng, Thẻ Danh Thiếp AI Kim Loại Tùy Chỉnh mang đến độ bền và sự sang trọng vượt trội. Thép không gỉ cắt chính xác phủ sơn tĩnh điện đen, chip NFC tích hợp và logo khắc laser liên kết với hồ sơ banana.cards có AI hỗ trợ.",
  eyebrow: "banana.cards · Kim loại",
  productName: "Thẻ Danh Thiếp AI Kim Loại Tùy Chỉnh",
  productIntro:
    "Được thiết kế để gây ấn tượng, Thẻ Danh Thiếp AI Kim Loại Tùy Chỉnh mang đến độ bền và sự uy tín vượt trội. Thép không gỉ cắt chính xác kết hợp với công nghệ NFC tạo nên một chiếc thẻ vừa mạnh mẽ vừa đẳng cấp — dành cho người chuyên nghiệp muốn nổi bật trong từng cú chạm.",
  ratingLabel: "4,87 · 68 đánh giá",
  perCardSuffix: "/ thẻ · Miễn phí vận chuyển toàn cầu cho đơn trên $50",
  saveBadge: (pct) => `Tiết kiệm ${pct}%`,
  bundles: BUNDLES_VI,
  bundleSection: {
    eyebrow: "Mua combo, tiết kiệm",
    heading: { pre: "Trang bị cho đội của bạn bằng", em: "kim loại cao cấp." },
    sub: "Mỗi thẻ có hồ sơ riêng — hoàn hảo cho đồng sáng lập, đội bán hàng hoặc cả công ty.",
    bundleLabel: "Mua combo, tiết kiệm",
    perCardLabel: "mỗi thẻ",
    selected: "Đã chọn",
    choose: "Chọn",
  },
  usps: [
    { icon: Smartphone, label: "Chia sẻ thông tin chỉ với một cú chạm" },
    { icon: Zap,        label: "Thu lại thông tin của họ chỉ với một cú nhấp" },
    { icon: RefreshCw,  label: "Không cần ứng dụng — hoạt động trên cả iPhone và Android" },
    { icon: ShieldCheck,label: "Bảo hành phần cứng trọn đời" },
  ],
  features: {
    eyebrow: "Vì sao chọn kim loại",
    heading: { pre: "Bền như két sắt.", em: "Thông minh như trợ lý." },
    items: [
      { icon: Gem,        title: "Thép không gỉ cao cấp",        desc: "Kim loại cắt chính xác phủ sơn tĩnh điện đen — có trọng lượng, chống xước và bền bỉ theo thời gian." },
      { icon: Sparkles,   title: "AI theo dõi tích hợp sẵn",     desc: "Mỗi cú chạm đều được lưu vào CRM banana, làm giàu dữ liệu và sẵn sàng cho AI soạn lời theo dõi." },
      { icon: CreditCard, title: "Logo khắc laser",              desc: "Logo, tên và chi tiết điểm nhấn được khắc vĩnh viễn — không phai, không bong." },
      { icon: Users,      title: "Triển khai dễ cho đội nhóm",   desc: "Đặt số lượng lớn thẻ kim loại cho cả đội — gắn thương hiệu, giao hàng và thanh toán cùng một lần." },
      { icon: Truck,      title: "Vận chuyển toàn cầu miễn phí", desc: "Đơn trên $50 được miễn phí giao đến hơn 100 quốc gia — 3-5 ngày làm việc." },
      { icon: BarChart3,  title: "Phân tích thời gian thực",     desc: "Xem mọi cú chạm, lượt nhấp và khách tiềm năng trên bảng điều khiển — biết sự kiện nào thực sự chuyển đổi." },
    ],
  },
  anatomy: {
    eyebrow: "Cấu tạo thẻ",
    heading: { pre: "Mọi chi tiết,", em: "được kỹ lưỡng thiết kế." },
    image: metalFeatures,
    imageAlt: "Cấu tạo Thẻ Danh Thiếp AI Kim Loại — logo khắc, chip NFC và mã QR",
    items: [
      { t: "Logo khắc tùy chỉnh", d: "Khắc laser trực tiếp lên bề mặt phủ sơn tĩnh điện — vĩnh viễn, cao cấp, mang đậm dấu ấn của bạn." },
      { t: "Kim loại phủ sơn tĩnh điện đen", d: "Lõi thép không gỉ với lớp phủ đen mờ — chống vân tay, chống xước và chống cong." },
      { t: "Chip NFC tích hợp", d: "Chạm để chia sẻ toàn bộ hồ sơ với mọi iPhone hoặc Android hiện đại — không cần ứng dụng." },
      { t: "Mã QR in sẵn", d: "Phương án dự phòng hoàn hảo cho thiết bị cũ hoặc ốp lưng dày — mọi cuộc gặp đều thành công." },
    ],
  },
  profile: {
    eyebrow: "Hồ sơ của bạn, theo cách của bạn",
    heading: { pre: "Một thẻ.", em: "Vô vàn bản dạng." },
    body: "Chọn bố cục, thêm màu sắc, ảnh, liên kết, dịch vụ và lời kêu gọi hành động — và chuyển đổi giữa hồ sơ công việc, dự án phụ hoặc tuyển dụng từ cùng một thẻ kim loại.",
    bullets: [
      "12+ bố cục & chủ đề do nhà thiết kế tạo",
      "Thêm video, thư viện, dịch vụ, FAQ",
      "Hồ sơ đa ngôn ngữ",
      "Biểu mẫu thu khách hàng tiềm năng trên mọi thẻ",
    ],
    image: metalProfile,
    imageAlt: "Hồ sơ kỹ thuật số tùy chỉnh trên iPhone",
  },
  compare: {
    eyebrow: "Giấy vs Kim loại",
    heading: { pre: "Thẻ giấy bị thất lạc.", em: "Kim loại được ghi nhớ." },
    sub: "Bạn xứng đáng có một thứ thực sự hoạt động — và người ta thực sự giữ lại.",
    paperLabel: "Thẻ danh thiếp giấy",
    productLabel: "Thẻ Danh Thiếp AI Kim Loại Tùy Chỉnh",
    paper: [
      "Phát thẻ giấy và hy vọng họ giữ",
      "Theo dõi thủ công vài ngày sau",
      "Quên mất bạn đã gặp ai và nói gì",
      "Không phân tích, không CRM, không tầm nhìn",
      "Bạn trông giống mọi người khác",
    ],
    product: [
      "Chạm một lần, thông tin được lưu tức thì",
      "Theo dõi thông minh tự động",
      "Mọi liên hệ được thu thập tự động",
      "Phân tích thời gian thực cho thấy sự kiện nào chuyển đổi",
      "Hiện đại, cao cấp, không thể quên",
    ],
  },
  steps: {
    eyebrow: "Cách hoạt động",
    heading: { pre: "Bạn không cần giải thích.", em: "Chỉ cần chạm." },
    sub: "Họ nhận được mọi thứ quan trọng — ngay lập tức.",
    stepLabel: (n) => `Bước ${n}`,
    items: [
      { title: "Chạm một lần để chia sẻ dễ dàng",  desc: "Chạm Thẻ Banana NFC vào bất kỳ điện thoại nào, không cần ứng dụng. Hồ sơ, liên kết và lịch của bạn mở ngay lập tức." },
      { title: "Họ lưu bạn ngay lập tức",          desc: "Họ thêm bạn thẳng vào danh bạ — thông tin của bạn ở nơi họ thực sự tìm: điện thoại của họ." },
      { title: "Bạn nhận lại thông tin của họ",    desc: "Mỗi khách tiềm năng mới tự động về bảng Banana của bạn và đồng bộ với CRM — không cần nhập tay." },
    ],
  },
  team: {
    eyebrow: "Dành cho đội nhóm",
    heading: { pre: "Được thiết kế cho", em: "đội bán chốt deal." },
    body: "Triển khai thẻ kim loại có thương hiệu cho cả đội, quản lý mọi hồ sơ trên một bảng điều khiển và đưa mọi liên hệ mới thẳng vào CRM chung.",
    cta: "Đặt cho đội của bạn",
    image: metalTeam,
    imageAlt: "Bảng điều khiển dành cho đội nhóm và doanh nghiệp",
    teamsHref: "/teams",
  },
  faq: {
    eyebrow: "Câu hỏi thường gặp",
    heading: { pre: "Câu hỏi", em: "thường gặp." },
    items: [
      { q: "Thẻ Danh Thiếp AI Kim Loại khác gì so với thẻ nhựa?", a: "Thẻ Danh Thiếp AI Kim Loại được chế tác từ thép không gỉ cao cấp, mang lại cảm giác có trọng lượng và chất lượng mà thẻ nhựa không thể sánh được. Ngoài độ bền vật lý, nó được tích hợp với hệ sinh thái AI của Banana, biến cuộc trao thẻ thông thường thành chuỗi thu thập và theo dõi khách tiềm năng tự động." },
      { q: "Làm sao để tùy chỉnh thẻ NFC kim loại của tôi?", a: "Sau khi thanh toán, chúng tôi sẽ gửi cho bạn một bản thiết kế ngắn. Tải logo lên, chọn màu nhấn và văn bản bạn muốn khắc — đội ngũ của chúng tôi sẽ làm mẫu, bạn duyệt, chúng tôi sản xuất. Hầu hết thẻ được giao trong vòng 5–7 ngày làm việc sau khi duyệt thiết kế." },
      { q: "Thẻ kim loại có hoạt động qua ốp lưng điện thoại không?", a: "Có. Ăng-ten NFC tích hợp được tinh chỉnh để đọc đáng tin cậy qua hầu hết các ốp silicone, da và nhựa tiêu chuẩn. Với ốp rất dày hoặc có lớp kim loại, bạn cũng có thể chia sẻ bằng mã QR in ở mặt sau." },
      { q: "Thẻ AI Kim Loại đi kèm những tính năng số nào?", a: "Mỗi thẻ bao gồm hồ sơ banana.cards: liên kết không giới hạn, hồ sơ đa ngôn ngữ, biểu mẫu thu khách tiềm năng, thẻ Apple & Google Wallet, phân tích thời gian thực, đồng bộ CRM và truy cập các công cụ AI (viết, làm giàu dữ liệu, soạn lời theo dõi)." },
      { q: "Tính năng AI theo dõi hoạt động ra sao?", a: "Khi ai đó chạm vào thẻ, thông tin của họ được làm giàu (công ty, vai trò, tín hiệu) và đẩy vào bảng điều khiển. AI sau đó soạn lời theo dõi cá nhân hóa dựa trên bối cảnh cuộc gặp — bạn duyệt, chỉnh và gửi trong vài giây." },
      { q: "Khắc laser trên thẻ kim loại có vĩnh viễn không?", a: "Hoàn toàn. Phần khắc được khắc trực tiếp vào bề mặt kim loại nên không phai, không bong, không mòn — nó là một phần của chính thẻ và được bảo hành phần cứng trọn đời." },
      { q: "Tôi có thể dùng Thẻ AI Kim Loại với CRM hiện tại không?", a: "Có. Banana đồng bộ trực tiếp với HubSpot, Salesforce, Pipedrive, Notion, Google Contacts và 50+ công cụ qua Zapier — mọi liên hệ mới chảy thẳng vào hệ thống bạn đã dùng." },
      { q: "Tôi có cần sạc thẻ kim loại hay dùng pin không?", a: "Không. Chip NFC là loại bị động — nó lấy một lượng điện rất nhỏ từ điện thoại đọc nó. Không có pin để thay và không có gì để sạc. Bao giờ." },
      { q: "Tôi có thể theo dõi số lần thẻ kim loại được sử dụng không?", a: "Có. Bảng điều khiển hiển thị mọi cú chạm và quét QR theo thời gian thực — bao gồm vị trí, thiết bị, thời gian và liên kết nào được nhấp — để bạn đo chính xác sự kiện và cuộc gặp nào mang lại kết quả." },
      { q: "Có bản sao lưu kỹ thuật số khi tôi không mang theo thẻ vật lý không?", a: "Có. Hồ sơ của bạn nằm tại một URL duy nhất hoạt động trên mọi thiết bị — chia sẻ qua liên kết, QR, chữ ký email hoặc thẻ Apple/Google Wallet. Thẻ kim loại là trải nghiệm, nhưng bạn không bao giờ bị mất quyền truy cập nếu không có nó." },
    ],
  },
  outro: {
    heading: { pre: "Mạng lưới với", em: "đẳng cấp cao cấp về phía bạn." },
    body: "Đặt trước 3 giờ chiều và chúng tôi giao vào ngày làm việc tiếp theo. Miễn phí vận chuyển toàn cầu cho đơn trên $50.",
    primaryCta: "Lấy thẻ Kim loại của tôi",
    secondaryCta: "Xem tất cả sản phẩm",
    shopHref: "/vi/shop",
  },
  buyBox: {
    addToCart: "Thêm vào giỏ",
  },
  gallery: GALLERY_VI,
};


const PRODUCT_URL_EL = "https://banana.cards/el/shop/metal";
const BUNDLES_EL = [
  { qty: 1, label: "1 κάρτα", perCard: 1.0 },
  { qty: 3, label: "3 κάρτες", perCard: 0.9, save: 10, badge: "Κέρδος 10%" },
  { qty: 5, label: "5 κάρτες", perCard: 0.82, save: 18, badge: "Δημοφιλέστερο" },
  { qty: 10, label: "10 κάρτες", perCard: 0.72, save: 28, badge: "Καλύτερη τιμή" },
] as const;
const GALLERY_EL = [
  { src: metalHero, alt: "Μεταλλική επαγγελματική κάρτα Custom Metal AI, εμπρός και πίσω όψη με NFC και QR" },
  { src: metalFeatures, alt: "Χαρακτηριστικά κάρτας Custom Metal — χαραγμένο λογότυπο, τσιπ NFC και κωδικός QR" },
  { src: metalProfile, alt: "Προσαρμόσιμα ψηφιακά προφίλ σε iPhone" },
  { src: metalTeam, alt: "Πίνακας διαχείρισης για ομάδες και επιχειρήσεις" },
];
export const metalDictEl: ProductPageDict = {
  ...metalDict,
  routePath: "/el/shop/metal",
  productUrl: PRODUCT_URL_EL,
  seo: {
    title: "Μεταλλική κάρτα NFC με AI — banana.cards",
    description: "Κορυφαία μεταλλική κάρτα NFC με μαύρη βαφή πούδρας, χαραγμένο λογότυπο, QR και κοινοποίηση προφίλ με AI. Αντοχή που διαρκεί, εντύπωση που μένει.",
    ogTitle: "Μεταλλική επαγγελματική κάρτα AI στα μέτρα σας",
    ogDescription: "Κορυφαία μεταλλική κάρτα NFC. Ένα άγγιγμα για άμεση κοινοποίηση και καταγραφή κάθε επαφής — με ενσωματωμένη επικοινωνία μέσω AI.",
    locale: "el_GR",
  },
  jsonLdDescription: "Σχεδιασμένη για να εντυπωσιάζει, η μεταλλική επαγγελματική κάρτα AI στα μέτρα σας συνδυάζει απαράμιλλη αντοχή και κύρος. Ανοξείδωτο ατσάλι κομμένο με ακρίβεια, μαύρη βαφή πούδρας, ενσωματωμένο τσιπ NFC και λογότυπο χαραγμένο με λέιζερ, συνδεδεμένα με το προφίλ σας στο banana.cards με υποστήριξη AI.",
  eyebrow: "banana.cards · Μέταλλο",
  productName: "Μεταλλική επαγγελματική κάρτα AI στα μέτρα σας",
  productIntro: "Σχεδιασμένη για να εντυπωσιάζει, η μεταλλική επαγγελματική κάρτα AI στα μέτρα σας προσφέρει απαράμιλλη αντοχή και κύρος. Το ανοξείδωτο ατσάλι ακριβείας συναντά την τεχνολογία NFC σε μια κάρτα με επιβλητική εμφάνιση και αίσθηση — για επαγγελματίες που θέλουν να ξεχωρίζουν σε κάθε άγγιγμα.",
  ratingLabel: "4,87 · 68 κριτικές",
  perCardSuffix: "/ κάρτα · Δωρεάν αποστολή παγκοσμίως για παραγγελίες άνω των $50",
  saveBadge: (pct) => `Κέρδος ${pct}%`,
  bundles: BUNDLES_EL,
  bundleSection: {
    eyebrow: "Περισσότερες κάρτες, μεγαλύτερο όφελος",
    heading: { pre: "Εξοπλίστε την ομάδα σας με", em: "μέταλλο κορυφαίας ποιότητας." },
    sub: "Κάθε κάρτα έχει το δικό της προφίλ — ιδανική για συνιδρυτές, ομάδες πωλήσεων ή ολόκληρες εταιρείες.",
    bundleLabel: "Πακέτα με όφελος", perCardLabel: "ανά κάρτα", selected: "Επιλεγμένο", choose: "Επιλέξτε",
  },
  usps: [
    { icon: Smartphone, label: "Μοιραστείτε τα στοιχεία σας με ένα άγγιγμα" },
    { icon: Zap, label: "Λάβετε τα στοιχεία τους με ένα κλικ" },
    { icon: RefreshCw, label: "Χωρίς εφαρμογή — λειτουργεί σε Apple και Android" },
    { icon: ShieldCheck, label: "Εγγύηση εφ’ όρου ζωής για την κάρτα" },
  ],
  features: {
    eyebrow: "Γιατί να επιλέξετε μέταλλο",
    heading: { pre: "Ανθεκτική σαν θησαυροφυλάκιο.", em: "Έξυπνη σαν βοηθός." },
    items: [
      { icon: Gem, title: "Ανοξείδωτο ατσάλι κορυφαίας ποιότητας", desc: "Μέταλλο κομμένο με ακρίβεια και μαύρο φινίρισμα βαφής πούδρας — στιβαρό, ανθεκτικό στις γρατζουνιές και στον χρόνο." },
      { icon: Sparkles, title: "Ενσωματωμένη επικοινωνία με AI", desc: "Κάθε άγγιγμα καταγράφεται στο CRM του Banana, με εμπλουτισμένα στοιχεία, έτοιμα για μηνύματα συνέχειας που συντάσσει το AI." },
      { icon: CreditCard, title: "Λογότυπο χαραγμένο με λέιζερ", desc: "Το λογότυπο, το όνομα και οι λεπτομέρειές σας χαράσσονται μόνιμα — δεν ξεθωριάζουν ούτε σβήνουν με την τριβή." },
      { icon: Users, title: "Εύκολη διάθεση σε όλη την ομάδα", desc: "Μαζικές παραγγελίες μεταλλικών καρτών για ολόκληρες ομάδες — με την επωνυμία σας, ενιαία αποστολή και τιμολόγηση." },
      { icon: Truck, title: "Δωρεάν αποστολή παγκοσμίως", desc: "Δωρεάν αποστολή για παραγγελίες άνω των $50 σε 100+ χώρες — σε 3–5 εργάσιμες ημέρες." },
      { icon: BarChart3, title: "Στατιστικά σε πραγματικό χρόνο", desc: "Δείτε κάθε άγγιγμα, κλικ και υποψήφιο πελάτη στον πίνακα ελέγχου σας — και ποιες εκδηλώσεις φέρνουν πραγματικά αποτελέσματα." },
    ],
  },
  anatomy: {
    eyebrow: "Η κάρτα από κοντά",
    heading: { pre: "Κάθε λεπτομέρεια,", em: "μελετημένη στην εντέλεια." },
    image: metalFeatures,
    imageAlt: "Ανατομία επαγγελματικής κάρτας Custom Metal AI — χαραγμένο λογότυπο, τσιπ NFC και κωδικός QR",
    items: [
      { t: "Το δικό σας χαραγμένο λογότυπο", d: "Χάραξη λέιζερ απευθείας στην επιφάνεια με βαφή πούδρας — μόνιμη, κομψή και με τη δική σας υπογραφή." },
      { t: "Μέταλλο με μαύρη βαφή πούδρας", d: "Πυρήνας από ανοξείδωτο ατσάλι με ματ μαύρη βαφή πούδρας — αντέχει σε δαχτυλιές, γρατζουνιές και λύγισμα." },
      { t: "Ενσωματωμένο τσιπ NFC", d: "Αγγίζετε την κάρτα σε οποιοδήποτε σύγχρονο iPhone ή Android και μοιράζεστε όλο το προφίλ σας — χωρίς εφαρμογή." },
      { t: "Τυπωμένος κωδικός QR", d: "Η ιδανική εναλλακτική για παλαιότερες συσκευές ή πιο χοντρές θήκες — ώστε κάθε γνωριμία να μετράει." },
    ],
  },
  profile: {
    eyebrow: "Το προφίλ σας, όπως το θέλετε",
    heading: { pre: "Μία κάρτα.", em: "Αμέτρητες επαγγελματικές ταυτότητες." },
    body: "Επιλέξτε διάταξη, προσθέστε χρώματα, φωτογραφία, συνδέσμους, υπηρεσίες και κουμπιά δράσης — και εναλλάξτε προφίλ για την εργασία, το παράλληλο εγχείρημά σας ή τις προσλήψεις, από την ίδια μεταλλική κάρτα.",
    bullets: ["12+ επαγγελματικά σχεδιασμένες διατάξεις και θέματα", "Προσθέστε βίντεο, συλλογή εικόνων, υπηρεσίες και συχνές ερωτήσεις", "Πολύγλωσσα προφίλ", "Φόρμα συλλογής στοιχείων υποψήφιων πελατών σε κάθε κάρτα"],
    image: metalProfile, imageAlt: "Προσαρμόσιμα ψηφιακά προφίλ σε iPhone",
  },
  compare: {
    eyebrow: "Χαρτί ή μέταλλο",
    heading: { pre: "Οι χάρτινες κάρτες χάνονται.", em: "Οι μεταλλικές μένουν αξέχαστες." },
    sub: "Αξίζετε κάτι που λειτουργεί πραγματικά — και που οι άλλοι θέλουν να κρατήσουν.",
    paperLabel: "Χάρτινες επαγγελματικές κάρτες", productLabel: "Μεταλλική επαγγελματική κάρτα AI στα μέτρα σας",
    paper: ["Μοιράζετε χάρτινες κάρτες και ελπίζετε να τις κρατήσουν", "Επικοινωνείτε ξανά χειροκίνητα, μέρες μετά", "Ξεχνάτε ποιον γνωρίσατε ή τι συζητήσατε", "Χωρίς στατιστικά, CRM ή εικόνα των αποτελεσμάτων", "Δεν ξεχωρίζετε από τους υπόλοιπους"],
    product: ["Ένα άγγιγμα και τα στοιχεία σας αποθηκεύονται αμέσως", "Αυτόματη, έξυπνη επικοινωνία μετά τη γνωριμία", "Κάθε επαφή καταγράφεται αυτόματα", "Στατιστικά σε πραγματικό χρόνο δείχνουν ποιες εκδηλώσεις αποδίδουν", "Σύγχρονη, κορυφαία ποιότητα που μένει αξέχαστη"],
  },
  steps: {
    eyebrow: "Πώς λειτουργεί",
    heading: { pre: "Δεν χρειάζονται εξηγήσεις.", em: "Απλώς αγγίζετε." },
    sub: "Όλα όσα έχουν σημασία φτάνουν στα χέρια τους — αμέσως.",
    stepLabel: (n) => `Βήμα ${n}`,
    items: [
      { title: "Ένα άγγιγμα για εύκολη κοινοποίηση", desc: "Αγγίζετε την κάρτα Banana NFC σε ένα smartphone, χωρίς να χρειάζεται εφαρμογή. Το προφίλ, οι σύνδεσμοι και το ημερολόγιό σας ανοίγουν αμέσως." },
      { title: "Σας αποθηκεύουν αμέσως", desc: "Σας προσθέτουν απευθείας στις επαφές τους — τα στοιχεία σας μένουν εκεί που πραγματικά τα αναζητούν: στο κινητό τους." },
      { title: "Λαμβάνετε και τα δικά τους στοιχεία", desc: "Κάθε νέος υποψήφιος πελάτης καταγράφεται αυτόματα στον πίνακα ελέγχου Banana και συγχρονίζεται με το CRM σας — χωρίς χειροκίνητη καταχώριση." },
    ],
  },
  team: {
    eyebrow: "Για ομάδες",
    heading: { pre: "Σχεδιασμένη για", em: "ομάδες που κλείνουν συμφωνίες." },
    body: "Προσφέρετε μεταλλικές κάρτες με την εταιρική σας ταυτότητα σε όλη την ομάδα, διαχειριστείτε κάθε προφίλ από έναν πίνακα ελέγχου και στείλτε κάθε νέα επαφή απευθείας στο κοινό σας CRM.",
    cta: "Παραγγείλετε για την ομάδα σας", image: metalTeam,
    imageAlt: "Πίνακας διαχείρισης για ομάδες και επιχειρήσεις", teamsHref: "/teams",
  },
  faq: {
    eyebrow: "Συχνές ερωτήσεις", heading: { pre: "Οι πιο συχνές", em: "ερωτήσεις σας." },
    items: [
      { q: "Σε τι διαφέρει η μεταλλική επαγγελματική κάρτα AI από τις πλαστικές;", a: "Κατασκευάζεται από ανοξείδωτο ατσάλι κορυφαίας ποιότητας, με βάρος και αίσθηση που οι πλαστικές κάρτες δεν μπορούν να προσφέρουν. Πέρα από την αντοχή της, ενσωματώνεται στο οικοσύστημα AI του Banana, μετατρέποντας μια απλή ανταλλαγή στοιχείων σε αυτόματη συλλογή υποψήφιων πελατών και επικοινωνία μετά τη γνωριμία." },
      { q: "Πώς προσαρμόζω τη μεταλλική επαγγελματική κάρτα NFC;", a: "Μετά την ολοκλήρωση της αγοράς, σας στέλνουμε μια σύντομη φόρμα με τις προτιμήσεις σχεδιασμού σας. Ανεβάζετε το λογότυπό σας και επιλέγετε χρώματα και κείμενο για χάραξη. Η ομάδα μας ετοιμάζει μακέτα, εσείς την εγκρίνετε και εμείς προχωράμε στην παραγωγή. Οι περισσότερες κάρτες αποστέλλονται εντός 5–7 εργάσιμων ημερών από την έγκριση του σχεδίου." },
      { q: "Λειτουργεί η μεταλλική κάρτα μέσα από θήκες κινητών;", a: "Ναι. Η ενσωματωμένη κεραία NFC είναι ρυθμισμένη ώστε να διαβάζεται αξιόπιστα μέσα από τις περισσότερες συνηθισμένες θήκες σιλικόνης, δέρματος και πλαστικού. Για πολύ χοντρές θήκες ή θήκες με μεταλλική πλάτη, μπορείτε να χρησιμοποιήσετε τον τυπωμένο κωδικό QR στην πίσω όψη." },
      { q: "Ποιες ψηφιακές λειτουργίες περιλαμβάνει η μεταλλική κάρτα AI;", a: "Κάθε κάρτα περιλαμβάνει προφίλ banana.cards: απεριόριστους συνδέσμους, πολύγλωσσα προφίλ, φόρμα συλλογής στοιχείων υποψήφιων πελατών, κάρτα για Apple Wallet και Google Wallet, στατιστικά σε πραγματικό χρόνο, συγχρονισμό CRM και πρόσβαση στα εργαλεία AI μας για σύνταξη κειμένων, εμπλουτισμό δεδομένων και προσχέδια μηνυμάτων συνέχειας." },
      { q: "Πώς λειτουργεί η επικοινωνία μετά τη γνωριμία με AI;", a: "Όταν κάποιος αγγίζει την κάρτα σας, τα στοιχεία του εμπλουτίζονται με εταιρεία, ρόλο και σχετικές ενδείξεις και μεταφέρονται στον πίνακα ελέγχου σας. Το AI συντάσσει στη συνέχεια ένα εξατομικευμένο μήνυμα με βάση το πλαίσιο της συνάντησης — το ελέγχετε, το επεξεργάζεστε και το στέλνετε σε δευτερόλεπτα." },
      { q: "Είναι μόνιμη η χάραξη λέιζερ στη μεταλλική κάρτα;", a: "Απολύτως. Η χάραξη γίνεται απευθείας στη μεταλλική επιφάνεια, οπότε δεν ξεθωριάζει, δεν σβήνει με την τριβή και δεν φθείρεται. Αποτελεί μέρος της ίδιας της κάρτας και καλύπτεται από την εγγύηση εφ’ όρου ζωής για την κάρτα." },
      { q: "Μπορώ να χρησιμοποιήσω τη μεταλλική κάρτα AI με το υπάρχον CRM μου;", a: "Ναι. Το Banana συγχρονίζεται απευθείας με HubSpot, Salesforce, Pipedrive, Notion και Google Contacts, καθώς και με 50+ εργαλεία μέσω Zapier — κάθε νέα επαφή μεταφέρεται κατευθείαν στο σύστημα που ήδη χρησιμοποιείτε." },
      { q: "Χρειάζεται φόρτιση ή μπαταρίες η μεταλλική κάρτα;", a: "Όχι. Το τσιπ NFC είναι παθητικό: αντλεί ελάχιστη ενέργεια από το smartphone που το διαβάζει. Δεν υπάρχουν μπαταρίες για αντικατάσταση ούτε κάτι που χρειάζεται φόρτιση. Ποτέ." },
      { q: "Μπορώ να παρακολουθώ πόσες φορές χρησιμοποιείται η μεταλλική κάρτα μου;", a: "Ναι. Ο πίνακας ελέγχου εμφανίζει κάθε άγγιγμα και σάρωση QR σε πραγματικό χρόνο, μαζί με την τοποθεσία, τη συσκευή, την ώρα και τους συνδέσμους που επιλέχθηκαν. Έτσι μετράτε με ακρίβεια ποιες εκδηλώσεις και συναντήσεις φέρνουν αποτελέσματα." },
      { q: "Υπάρχει ψηφιακή εναλλακτική αν δεν έχω μαζί μου την κάρτα;", a: "Ναι. Το προφίλ σας διαθέτει μοναδικό URL που λειτουργεί σε κάθε συσκευή. Μοιραστείτε το με σύνδεσμο, QR, υπογραφή email ή κάρτα στο Apple Wallet ή στο Google Wallet. Η μεταλλική κάρτα προσφέρει την εμπειρία, αλλά έχετε πάντα πρόσβαση και χωρίς αυτήν." },
    ],
  },
  outro: {
    heading: { pre: "Δικτυωθείτε με", em: "το κύρος που σας αξίζει." },
    body: "Παραγγείλετε πριν τις 15:00 και αποστέλλουμε την επόμενη εργάσιμη ημέρα. Δωρεάν αποστολή παγκοσμίως για παραγγελίες άνω των $50.",
    primaryCta: "Αποκτήστε τη μεταλλική κάρτα σας", secondaryCta: "Δείτε όλα τα προϊόντα", shopHref: "/el/shop",
  },
  buyBox: { addToCart: "Προσθήκη στο καλάθι" },
  gallery: GALLERY_EL,
};
